import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import { generatePricingRecommendations } from '../../../services/aiAnalytics';
import toast from 'react-hot-toast';

const DynamicPricingAlerts = ({ pricingSuggestions = [], vacantUnits = [] }) => {
  const navigate = useNavigate();
  const [aiPricing, setAiPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (error) {
      toast?.error(error?.message || 'Failed to generate pricing recommendations');
    }
  }, [error]);

  useEffect(() => {
    if (vacantUnits?.length > 0) {
      loadPricingAnalysis();
    }
  }, [vacantUnits]);

  const loadPricingAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const pricingData = {
        vacantUnits: vacantUnits?.slice(0, 5) || [],
        marketData: {
          averageOccupancy: 85,
          marketTrend: 'stable',
          demandLevel: 'medium'
        },
        competitorPricing: {
          averageRate: 5000,
          range: { min: 3500, max: 7500 }
        },
        seasonalTrends: {
          currentSeason: 'peak',
          forecast: 'increasing'
        }
      };
      
      const result = await generatePricingRecommendations(pricingData);
      setAiPricing(result);
    } catch (err) {
      console.error('Pricing analysis error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const getAdjustmentColor = (percentage) => {
    if (percentage > 0) return 'text-success-600';
    if (percentage < 0) return 'text-danger-600';
    return 'text-muted-foreground';
  };

  const getAdjustmentIcon = (percentage) => {
    if (percentage > 0) return 'TrendingUp';
    if (percentage < 0) return 'TrendingDown';
    return 'Minus';
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-success-100 rounded-lg">
            <Icon name="DollarSign" className="w-5 h-5 text-success-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Dynamic Pricing</h3>
            <p className="text-sm text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-success-100 rounded-lg">
            <Icon name="DollarSign" className="w-5 h-5 text-success-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">AI Dynamic Pricing</h3>
            <p className="text-sm text-muted-foreground">Powered by OpenAI</p>
          </div>
        </div>
        <button
          onClick={loadPricingAnalysis}
          disabled={loading}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
          title="Refresh analysis"
        >
          <Icon name="RefreshCw" className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {aiPricing?.recommendations?.length > 0 ? (
        <>
          {/* Overall Strategy */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Target" className="w-4 h-4" />
              Overall Strategy
            </h4>
            <p className="text-sm text-muted-foreground">
              {aiPricing?.overallStrategy}
            </p>
          </div>

          {/* Top Recommendations */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Zap" className="w-4 h-4" />
              Top Pricing Opportunities
            </h4>
            <div className="space-y-3">
              {aiPricing?.recommendations?.slice(0, 3)?.map((rec, index) => {
                const unit = vacantUnits?.find(u => u?.id === rec?.unitId);
                return (
                  <div key={index} className="bg-muted border border-border rounded-lg p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-sm text-foreground">
                          {unit?.unitNumber || `Unit ${rec?.unitId}`}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {unit?.sqft ? `${unit?.sqft} sqft` : ''} {unit?.floor ? `• Floor ${unit?.floor}` : ''}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`flex items-center gap-1 text-sm font-semibold ${getAdjustmentColor(rec?.adjustmentPercentage)}`}>
                          <Icon name={getAdjustmentIcon(rec?.adjustmentPercentage)} className="w-4 h-4" />
                          {rec?.adjustmentPercentage > 0 ? '+' : ''}{rec?.adjustmentPercentage?.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Current:</span>
                        <span className="font-medium text-foreground ml-1">
                          ${rec?.currentPrice?.toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Suggested:</span>
                        <span className="font-medium text-success-600 ml-1">
                          ${rec?.suggestedPrice?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mb-1">
                      <strong>Reasoning:</strong> {rec?.reasoning}
                    </div>
                    <div className="text-xs text-foreground mb-1">
                      <strong>Impact:</strong> {rec?.expectedImpact}
                    </div>
                    <div className="text-xs text-primary-600">
                      <strong>Timing:</strong> {rec?.timing}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Button
            onClick={() => navigate('/inventory-management')}
            variant="outline"
            className="w-full"
            iconName="ArrowRight"
            iconPosition="right"
          >
            View All Inventory
          </Button>
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="DollarSign" className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No pricing recommendations available</p>
        </div>
      )}
    </Card>
  );
};

export default DynamicPricingAlerts;